import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { forkJoin, Subject, takeUntil } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { AdminService } from '@generated/services/admin.service';
import { AgentsService } from '@generated/services/agents.service';
import { AgentSummary } from '@generated/models/agent-summary';
import { ModelsService } from '@generated/services/models.service';
import { BenchmarkDetailDto } from '@generated/models/benchmark-detail-dto';
import { BenchmarkModelVersionLinkDto } from '@generated/models/benchmark-model-version-link-dto';
import { BenchmarkModelVersionLinkInput } from '@generated/models/benchmark-model-version-link-input';
import { GetModelsHierarchyResult } from '@generated/models/get-models-hierarchy-result';
import { ModelTypeHierarchy } from '@generated/models/model-type-hierarchy';
import { ChatMessageDto } from '@generated/models/chat-message-dto';
import { NotificationService } from '@core/services/notification.service';

import { ChatHistoryViewComponent } from '@shared/components/chat-history-view/chat-history-view.component';
import { ModelSelectorButton } from '@shared/components/model-selector-button/model-selector-button';
import type { TesterSelection } from '@shared/components/model-tester/model-tester.component';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { BenchmarkSttSamplesComponent } from '@features/admin/benchmark-stt-samples/benchmark-stt-samples.component';
import { BenchmarkSttSampleDto } from '@generated/models/benchmark-stt-sample-dto';
import { LanguageLocaleDto } from '@generated/models/language-locale-dto';
import { LanguagesService } from '@generated/services/languages.service';
import { dictToValueEntries, valueEntriesToDict } from '@shared/utils/openapi-schema-utils';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';

/**
 * Создание / редактирование бенчмарка (LLM или STT).
 *
 * URL:
 *   /admin/benchmarks/new
 *   /admin/benchmarks/:id/edit
 *
 * Форма:
 *   - Base: title, description, model_type (LLM/STT), iterations, auto_benchmark_enabled
 *   - LLM-only: temperature, benchmark_messages (system + dialog)
 *   - Linked model versions: multiselect через ModelSelector, сохраняются
 *     как BenchmarkModelVersionLinkInput[] в model_version_links.
 *   - STT samples management вынесен в отдельный шаг (upload через presigned URL
 *     + POST /stt-samples) — пока только показываем текущий список; загрузку
 *     новых семплов добавим отдельным подкомпонентом.
 */
@Component({
  selector: 'app-benchmark-edit',
  standalone: true,
  imports: [
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantActionDirective,
    FormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    SelectModule,
    ToggleSwitchModule,
    TagModule,
    TooltipModule,
    TableModule,
    ProgressSpinnerModule,
    ChatHistoryViewComponent,
    ModelSelectorButton,
    DeleteConfirmationComponent,
    BenchmarkSttSamplesComponent,
  ],
  templateUrl: './benchmark-edit.component.html',
  styleUrl: './benchmark-edit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BenchmarkEditComponent implements OnInit, OnDestroy {
  private adminService = inject(AdminService);
  private agentsService = inject(AgentsService);
  private modelsService = inject(ModelsService);
  private languagesService = inject(LanguagesService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroy$ = new Subject<void>();

  readonly benchmarkId = signal<string | null>(null);
  readonly isEditMode = computed(() => this.benchmarkId() !== null);

  readonly isLoading = signal<boolean>(false);
  readonly isSaving = signal<boolean>(false);

  // Form state
  readonly title = signal<string>('');
  readonly description = signal<string>('');
  readonly modelTypeId = signal<string | null>(null);
  readonly modelTypeCode = signal<string>('');
  readonly iterations = signal<number>(1);
  readonly autoBenchmarkEnabled = signal<boolean>(false);
  /** Язык бенча — обязателен для STT (эталоны на этом языке, WER считается
   *  по нему), опционально для LLM. Один язык на весь бенчмарк. */
  readonly languageLocaleId = signal<string | null>(null);
  readonly locales = signal<LanguageLocaleDto[]>([]);
  readonly localeOptions = computed(() =>
    this.locales().map(l => ({
      label: `${l.locale_flag ? l.locale_flag + ' ' : ''}${l.language_name} · ${l.locale_name}`,
      value: l.id ?? '',
    }))
  );

  // Agent seed picker (LLM only). Список агентов подгружаем один раз при
  // isLlm=true, при выборе агента копируем system_prompt в textarea и
  // первое сообщение primary-локали (first_message) в первую Assistant
  // реплику стартового диалога — тем же паттерном, что делал старый
  // model-properties-drawer до SKN-290.
  readonly agents = signal<AgentSummary[]>([]);
  readonly isLoadingAgents = signal<boolean>(false);
  readonly seedAgentId = signal<string | null>(null);
  readonly isSeedingFromAgent = signal<boolean>(false);
  readonly agentOptions = computed(() =>
    this.agents().map(a => ({ label: a.title || a.id || '', value: a.id }))
  );

  readonly systemPrompt = signal<string>('');
  readonly dialogMessages = signal<ChatMessageDto[]>([]);
  /** Live snapshot из бэка (только при редактировании), нужен чтобы
   *  показать stt_samples и уже привязанные версии. */
  readonly loaded = signal<BenchmarkDetailDto | null>(null);
  readonly linkedVersions = signal<BenchmarkModelVersionLinkInput[]>([]);
  readonly linkedVersionIds = computed<string[]>(() =>
    this.linkedVersions().map(l => l.model_version_id).filter((x): x is string => !!x)
  );
  /** Map versionId → sampling overrides для прокидки в тестер, чтобы при
   *  повторном открытии tester'а слайдеры показывали сохранённые значения,
   *  а не дефолты. */
  readonly linkedSamplingParams = computed<Record<string, {
    temperature?: number | null;
    topK?: number | null;
    topP?: number | null;
    maxOutputTokens?: number | null;
    additionalProperties?: Record<string, any> | null;
  }>>(() => {
    const out: Record<string, {
      temperature?: number | null;
      topK?: number | null;
      topP?: number | null;
      maxOutputTokens?: number | null;
      additionalProperties?: Record<string, any> | null;
    }> = {};
    for (const l of this.linkedVersions()) {
      if (!l.model_version_id) continue;
      out[l.model_version_id] = {
        temperature: l.temperature,
        topK: l.top_k,
        topP: l.top_p,
        maxOutputTokens: l.max_output_tokens,
        additionalProperties: l.additional_properties
          ? valueEntriesToDict(l.additional_properties)
          : null,
      };
    }
    return out;
  });

  // Data
  readonly modelsHierarchy = signal<GetModelsHierarchyResult | null>(null);
  /** Флаг «показать все модели, не только рекомендованные». Прокидывается
   *  в model-selector-button (двусторонняя связь через includeAllChange)
   *  и в apiModelsHierarchyGet(). По умолчанию true — админ в редакторе
   *  бенча должен видеть весь каталог. */
  readonly includeAll = signal<boolean>(true);
  /** Только LLM+STT типы — TTS/VAD бенчить нечем. */
  readonly modelTypeOptions = computed<Array<{ label: string; value: string; code: string }>>(() => {
    const types = this.modelsHierarchy()?.model_types ?? [];
    return types
      .filter((t: ModelTypeHierarchy) => ['llm', 'stt'].includes((t.code || '').toLowerCase()))
      .map(t => ({
        label: `${t.name || t.code} (${(t.code || '').toUpperCase()})`,
        value: t.id || '',
        code: (t.code || '').toLowerCase()
      }));
  });

  readonly isLlm = computed(() => this.modelTypeCode() === 'llm');
  readonly isStt = computed(() => this.modelTypeCode() === 'stt');

  // Delete state — тот же паттерн что в benchmarks-list: шарный
  // <app-delete-confirmation> с loading-флагом.
  readonly deleteVisible = signal<boolean>(false);
  readonly isDeleting = signal<boolean>(false);
  // Отдельный run-in-progress индикатор для «Сохранить и запустить»
  // (Save → PUT, потом POST /runs).
  readonly isSavingAndRunning = signal<boolean>(false);

  /** Актуальный набор STT-sample id-ов, которые останутся привязаны к бенчу
   *  после Save. Инициализируется из detail'а, локально мутируется при
   *  add/remove; в UpdateBenchmarkCommand улетает как `stt_sample_ids` с
   *  full-replace семантикой (лишние — удаляются на бэке). */
  readonly sttSampleIds = signal<string[]>([]);
  /** Отображаемый список сэмплов = loaded.stt_samples ∩ sttSampleIds
   *  (юзер мог локально удалить строку — она сразу пропадает из UI, а Save
   *  синхронизирует бэк). */
  readonly sttSamples = computed(() => {
    const kept = new Set(this.sttSampleIds());
    return (this.loaded()?.stt_samples ?? []).filter(s => s.id && kept.has(s.id));
  });

  /** Форма валидна для отправки. */
  readonly canSave = computed(() =>
    (this.title().trim().length > 0)
    && !!this.modelTypeId()
    && (this.iterations() ?? 0) >= 1
    // Для STT язык обязателен — эталонные семплы и WER считаются под него.
    // Для LLM пока опционально.
    && (!this.isStt() || !!this.languageLocaleId())
    && !this.isSaving()
  );

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    this.benchmarkId.set(id);
    this.loadBootstrap(id);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadBootstrap(id: string | null): void {
    this.isLoading.set(true);
    this.cdr.markForCheck();

    // Всегда нужен hierarchy — для селектора model_type и для селектора версий.
    // При edit-mode параллельно тянем существующий бенч.
    // includeAll управляется свитчем "Только рекомендованные" внутри
    // model-selector-button; без явного параметра бэк дефолтит на false
    // и показывает только рекомендованные — админу это неудобно.
    const hierarchy$ = this.modelsService.apiModelsHierarchyGet({ includeAll: this.includeAll() });
    const locales$ = this.languagesService.apiLanguagesLanguageLocalesGet();
    const detail$ = id ? this.adminService.apiAdminBenchmarksBenchmarkIdGet({ benchmarkId: id }) : null;

    if (detail$) {
      forkJoin({ hierarchy: hierarchy$, locales: locales$, detail: detail$ })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: ({ hierarchy, locales, detail }) => {
            this.modelsHierarchy.set(hierarchy);
            this.locales.set(locales?.language_locales ?? []);
            this.hydrateFromDetail(detail);
            this.isLoading.set(false);
            this.cdr.markForCheck();
          },
          error: (e) => this.handleLoadError(e),
        });
    } else {
      forkJoin({ hierarchy: hierarchy$, locales: locales$ })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: ({ hierarchy, locales }) => {
            this.modelsHierarchy.set(hierarchy);
            this.locales.set(locales?.language_locales ?? []);
            this.applyDefaultsForNew();
            this.isLoading.set(false);
            this.cdr.markForCheck();
          },
          error: (e) => this.handleLoadError(e),
        });
    }
  }

  private handleLoadError(error: unknown): void {
    console.error('Failed to load benchmark data:', error);
    this.notificationService.showError(
      $localize`:@@benchmarks.edit.loadFailed:Не удалось загрузить данные бенчмарка`
    );
    this.isLoading.set(false);
    this.cdr.markForCheck();
  }

  private applyDefaultsForNew(): void {
    // Default model type = LLM (первый в опциях).
    const opts = this.modelTypeOptions();
    const llm = opts.find(o => o.code === 'llm') ?? opts[0];
    if (llm) {
      this.modelTypeId.set(llm.value);
      this.modelTypeCode.set(llm.code);
    }
    this.iterations.set(1);
    if (this.isLlm()) this.loadAgentsOnce();
  }

  private hydrateFromDetail(detail: BenchmarkDetailDto): void {
    this.loaded.set(detail);
    this.title.set(detail.title ?? '');
    this.description.set(detail.description ?? '');
    this.modelTypeId.set(detail.model_type_id ?? null);
    this.modelTypeCode.set((detail.model_type_code ?? '').toLowerCase());
    this.iterations.set(detail.iterations ?? 1);
    this.autoBenchmarkEnabled.set(detail.auto_benchmark_enabled ?? false);
    this.languageLocaleId.set(detail.language_locale_id ?? null);
    const split = this.splitMessages(detail.benchmark_messages ?? null);
    this.systemPrompt.set(split.systemPrompt);
    this.dialogMessages.set(split.dialog);
    this.linkedVersions.set(
      (detail.model_versions ?? []).map((l: BenchmarkModelVersionLinkDto) => ({
        model_version_id: l.model_version_id,
        additional_properties: l.additional_properties ?? null,
        temperature: l.temperature ?? null,
        max_output_tokens: l.max_output_tokens ?? null,
        top_k: l.top_k ?? null,
        top_p: l.top_p ?? null,
      }))
    );
    this.sttSampleIds.set(
      (detail.stt_samples ?? [])
        .map(s => s.id)
        .filter((id): id is string => !!id)
    );
    if (this.isLlm()) this.loadAgentsOnce();
  }

  /** System message → отдельная textarea, всё остальное — в редактор
   *  диалога. Формат совпадает с тем, что делает model-properties-drawer. */
  private splitMessages(source: ChatMessageDto[] | null): { systemPrompt: string; dialog: ChatMessageDto[] } {
    if (!source?.length) return { systemPrompt: '', dialog: [] };
    const systemParts: string[] = [];
    const dialog: ChatMessageDto[] = [];
    for (const m of source) {
      if (m.role === 'System') {
        if (m.content) systemParts.push(m.content);
      } else {
        dialog.push(m);
      }
    }
    return { systemPrompt: systemParts.join('\n\n'), dialog };
  }

  private composeMessages(): ChatMessageDto[] | null {
    const out: ChatMessageDto[] = [];
    const sys = this.systemPrompt().trim();
    if (sys) out.push({ role: 'System', content: sys, kind: 'Message' });
    for (const m of this.dialogMessages()) {
      if ((m.content ?? '').trim().length === 0) continue;
      out.push(m);
    }
    return out.length > 0 ? out : null;
  }

  /** Свитч «Только рекомендованные» → выключен = includeAll=true. Пере-
   *  тягиваем hierarchy с новым флагом, чтобы каталог реально изменился.
   *  Селектор уже перерисует список сам благодаря input reactivity. */
  onIncludeAllChange(newIncludeAll: boolean): void {
    if (newIncludeAll === this.includeAll()) return;
    this.includeAll.set(newIncludeAll);
    this.modelsService.apiModelsHierarchyGet({ includeAll: newIncludeAll })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (hierarchy) => {
          this.modelsHierarchy.set(hierarchy);
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to reload hierarchy on includeAll change:', error);
        }
      });
  }

  onTypeChange(newTypeId: string | null): void {
    this.modelTypeId.set(newTypeId);
    const code = this.modelTypeOptions().find(o => o.value === newTypeId)?.code ?? '';
    this.modelTypeCode.set(code);
    // При смене типа снимаем привязку версий — они у другого типа не валидны.
    this.linkedVersions.set([]);
    // Если стал LLM — подгрузим агентов для seed-picker'а.
    if (this.isLlm()) this.loadAgentsOnce();
  }

  /**
   * Callback от app-model-selector в режиме multiSelect. Каждая выбранная
   * версия становится BenchmarkModelVersionLinkInput. additional_properties
   * оставляем null — редактируем per-link на отдельном шаге (если будет
   * нужно).
   */
  /**
   * Callback от ModelSelectorButton с новым набором versionId'ов.
   * Сохраняем существующие per-link overrides (temperature/top_k/…) для
   * версий, которые остались в наборе; для новых — инициализируем
   * null'ами (бэк упадёт на defaults бенчмарка).
   */
  onLinkedVersionsChange(ids: string | string[]): void {
    const list = Array.isArray(ids) ? ids : [ids];
    const existing = new Map(
      this.linkedVersions()
        .filter(l => !!l.model_version_id)
        .map(l => [l.model_version_id!, l])
    );
    this.linkedVersions.set(list.map(id => existing.get(id) ?? {
      model_version_id: id,
      additional_properties: null,
      temperature: null,
      max_output_tokens: null,
      top_k: null,
      top_p: null,
    }));
  }

  /** Тестерный drawer эмитит TesterSelection когда админ жмёт Select. Для
   *  benchmark-edit мы используем это событие чтобы сохранить sampling
   *  overrides (temperature/topK/topP/maxOutputTokens), которые админ
   *  выставил слайдерами в тестере. Ссылка на версию добавляется автомати-
   *  чески (если её ещё нет в linkedVersions) — так один Select работает
   *  и как «выбрать», и как «применить настройки». */
  onTesterSelected(selection: TesterSelection): void {
    const list = this.linkedVersions();
    const existing = list.find(l => l.model_version_id === selection.versionId);
    const patch: BenchmarkModelVersionLinkInput = {
      ...(existing ?? { model_version_id: selection.versionId, additional_properties: null }),
      additional_properties: selection.additionalProperties
        ? dictToValueEntries(selection.additionalProperties)
        : null,
      temperature: selection.temperature,
      max_output_tokens: selection.maxOutputTokens,
      top_k: selection.topK,
      top_p: selection.topP,
    };
    if (existing) {
      this.linkedVersions.set(list.map(l =>
        l.model_version_id === selection.versionId ? patch : l));
    } else {
      this.linkedVersions.set([...list, patch]);
    }
  }

  onDialogChange(messages: ChatMessageDto[]): void {
    this.dialogMessages.set(messages);
  }

  clearDialog(): void {
    this.dialogMessages.set([]);
  }

  /** Загружаем список агентов один раз при первом обращении. Дальше
   *  список кешируется в signal'e; при смене типа модели (когда
   *  agent-picker становится актуален) уже готов. */
  loadAgentsOnce(): void {
    if (this.agents().length > 0 || this.isLoadingAgents()) return;
    this.isLoadingAgents.set(true);
    this.agentsService.apiAgentsGet()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.agents.set(result.agents ?? []);
          this.isLoadingAgents.set(false);
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to load agents for benchmark seed:', error);
          this.isLoadingAgents.set(false);
          this.cdr.markForCheck();
        }
      });
  }

  /** Копируем system_prompt агента в textarea + first_message primary-локали
   *  в первую Assistant реплику. Замещает то что было в форме — клик по
   *  Fill-кнопке — explicit регенерация. */
  fillFromAgent(agentId: string | null): void {
    if (!agentId) return;
    this.isSeedingFromAgent.set(true);
    this.cdr.markForCheck();
    this.agentsService.apiAgentsIdGet({ id: agentId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agent) => {
          this.systemPrompt.set((agent.system_prompt || '').trim());
          const primaryLocale = (agent.language_locales || [])[0];
          const firstMessage = (primaryLocale?.first_message || '').trim();
          this.dialogMessages.set(
            firstMessage
              ? [{ role: 'Assistant', content: firstMessage, kind: 'Message' }]
              : []
          );
          this.isSeedingFromAgent.set(false);
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Failed to fetch agent for benchmark seed:', error);
          this.notificationService.showError(
            $localize`:@@benchmarks.edit.seedFailed:Не удалось загрузить агента`
          );
          this.isSeedingFromAgent.set(false);
          this.cdr.markForCheck();
        }
      });
  }

  cancel(): void {
    this.router.navigate(['/admin/benchmarks']);
  }

  save(): void {
    this.doSave(false);
  }

  /** «Сохранить и запустить»: сначала PUT (или POST для новых), после
   *  успеха — POST /benchmarks/{id}/runs (без versionId → парент запустит
   *  все привязанные версии). Дальше — navigate в /runs страницу.  */
  saveAndRun(): void {
    this.doSave(true);
  }

  private doSave(runAfter: boolean): void {
    if (!this.canSave()) return;

    const messages = this.isLlm() ? this.composeMessages() : null;

    const body = {
      title: this.title().trim(),
      description: this.description().trim() || null,
      model_type_id: this.modelTypeId()!,
      iterations: this.iterations() ?? 1,
      auto_benchmark_enabled: this.autoBenchmarkEnabled(),
      language_locale_id: this.languageLocaleId(),
      benchmark_messages: messages,
      model_version_links: this.linkedVersions().length > 0 ? this.linkedVersions() : null,
      // STT-only. Full-replace: null — не трогать, [] — очистить всё,
      // non-empty — оставить только эти id. На создании (POST) поле игнорим —
      // сэмплы заливаются отдельным endpoint'ом уже после сохранения бенча.
      stt_sample_ids: this.isEditMode() && this.isStt() ? this.sttSampleIds() : null,
    };

    this.isSaving.set(true);
    if (runAfter) this.isSavingAndRunning.set(true);
    this.cdr.markForCheck();

    const id = this.benchmarkId();
    const call$ = id
      ? this.adminService.apiAdminBenchmarksBenchmarkIdPut({
          benchmarkId: id,
          body: { ...body, id },
        })
      : this.adminService.apiAdminBenchmarksPost({ body });

    call$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (detail) => {
        this.notificationService.showSuccess(
          id
            ? $localize`:@@benchmarks.edit.updated:Бенчмарк обновлён`
            : $localize`:@@benchmarks.edit.created:Бенчмарк создан`
        );
        this.isSaving.set(false);
        this.cdr.markForCheck();
        const savedId = id ?? detail?.id ?? null;

        if (runAfter && savedId) {
          this.enqueueRunAndNavigate(savedId);
          return;
        }

        // После создания без run'а — редирект в edit-mode для STT-семплов.
        if (!id && detail?.id) {
          this.router.navigate(['/admin/benchmarks', detail.id, 'edit']);
        } else {
          this.hydrateFromDetail(detail);
        }
      },
      error: (error) => {
        console.error('Failed to save benchmark:', error);
        this.notificationService.showError(
          $localize`:@@benchmarks.edit.saveFailed:Не удалось сохранить бенчмарк`
        );
        this.isSaving.set(false);
        this.isSavingAndRunning.set(false);
        this.cdr.markForCheck();
      },
    });
  }

  private enqueueRunAndNavigate(id: string): void {
    this.adminService.apiAdminBenchmarksBenchmarkIdRunsPost({ benchmarkId: id, body: {} })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSavingAndRunning.set(false);
          this.notificationService.showSuccess(
            $localize`:@@benchmarks.edit.runStarted:Прогон стартанул. Открывается история...`
          );
          this.router.navigate(['/admin/benchmarks', id, 'runs']);
        },
        error: (error) => {
          console.error('Failed to enqueue benchmark run:', error);
          this.isSavingAndRunning.set(false);
          this.notificationService.showError(
            $localize`:@@benchmarks.edit.runFailed:Сохранили, но запуск сорвался`
          );
          this.cdr.markForCheck();
        }
      });
  }

  // ===== Delete =====

  onDeleteClick(): void {
    this.deleteVisible.set(true);
  }

  onDeleteConfirm(): void {
    const id = this.benchmarkId();
    if (!id) return;
    this.isDeleting.set(true);
    this.adminService.apiAdminBenchmarksBenchmarkIdDelete({ benchmarkId: id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isDeleting.set(false);
          this.deleteVisible.set(false);
          this.notificationService.showSuccess(
            $localize`:@@benchmarks.edit.deleted:Бенч удалён`
          );
          this.router.navigate(['/admin/benchmarks']);
        },
        error: (error) => {
          console.error('Failed to delete benchmark:', error);
          this.isDeleting.set(false);
          this.notificationService.showError(
            $localize`:@@benchmarks.edit.deleteFailed:Не удалось удалить бенч`
          );
          this.cdr.markForCheck();
        }
      });
  }

  onDeleteCancel(): void {
    if (this.isDeleting()) return;
    this.deleteVisible.set(false);
  }

  /** Callback от app-benchmark-stt-samples после успешной заливки. Мутируем
   *  loaded.stt_samples in-place, без re-fetch'а detail'а — subcomponent
   *  сразу отобразит новую строку. */
  onSttSampleAdded(sample: BenchmarkSttSampleDto): void {
    const current = this.loaded();
    if (!current) return;
    this.loaded.set({
      ...current,
      stt_samples: [...(current.stt_samples ?? []), sample],
    });
    if (sample.id) {
      this.sttSampleIds.set([...this.sttSampleIds(), sample.id]);
    }
    this.cdr.markForCheck();
  }

  /** Локальное удаление сэмпла — убираем id из актуального набора. Реальный
   *  soft-delete на бэке произойдёт при Save (full-replace через
   *  stt_sample_ids). Отменить можно только Cancel'ом всей формы. */
  onSttSampleRemove(sampleId: string): void {
    if (!sampleId) return;
    this.sttSampleIds.set(this.sttSampleIds().filter(id => id !== sampleId));
    this.cdr.markForCheck();
  }

  // Template helpers
  caseLabel(code: string | null | undefined): string {
    switch ((code || '').toLowerCase()) {
      case 'quiet': return $localize`:@@benchmarks.stt.case.quiet:Тихо`;
      case 'roadside': return $localize`:@@benchmarks.stt.case.roadside:У дороги`;
      case 'car': return $localize`:@@benchmarks.stt.case.car:В машине`;
      case 'tv': return $localize`:@@benchmarks.stt.case.tv:У ТВ`;
      case 'speakerphone': return $localize`:@@benchmarks.stt.case.speaker:Громкая связь`;
      default: return code || '';
    }
  }

  formatDurationMs(value: number | null | undefined): string {
    if (value == null) return '';
    const s = value / 1000;
    return `${s.toFixed(1)}s`;
  }

  formatMs(v: number | null | undefined): string { return v == null ? '—' : `${Math.round(v)} ms`; }
  formatTps(v: number | null | undefined): string { return v == null ? '—' : `${v.toFixed(1)} t/s`; }
  formatErrorRate(v: number | null | undefined): string {
    return v == null ? '—' : `${(v * 100).toFixed(1)}%`;
  }
  formatRtfx(v: number | null | undefined): string {
    return v == null ? '—' : `${v.toFixed(2)}×`;
  }
  formatDate(v: string | null | undefined): string {
    if (!v) return '';
    try { return new Date(v).toLocaleString(); } catch { return v; }
  }

  runStatusSeverity(s: string | null | undefined): 'info' | 'success' | 'warn' | 'danger' | 'secondary' {
    switch ((s || '').toLowerCase()) {
      case 'completed': return 'success';
      case 'failed': return 'danger';
      case 'running': return 'warn';
      case 'initial': return 'info';
      case 'stopped': return 'secondary';
      default: return 'secondary';
    }
  }

  runTriggerLabel(t: string | null | undefined): string {
    switch ((t || '').toLowerCase()) {
      case 'manual': return $localize`:@@benchmarks.trigger.manual:Manual`;
      case 'scheduled': return $localize`:@@benchmarks.trigger.scheduled:Scheduled`;
      default: return t || '';
    }
  }
}
